import React, { Children, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import { IMapProps, InfoWindow, Map } from "google-maps-react";
import { useIntl } from "react-intl";

import BaseMapStyle from "@mapmycustomers/shared/enum/map/BaseMapStyle";
import MarkerIcon from "@mapmycustomers/shared/enum/MarkerIcon";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { AnyEntity, Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import GeoPoint from "@mapmycustomers/shared/types/shapes/GeoPoint";
import { PickWithType } from "@mapmycustomers/shared/util/ts";
import { LoadingSpinner } from "@mapmycustomers/ui";

import useBasemapStyleOptions from "@app/component/map/utils/useBasemapStyleOptions";
import Theme from "@app/enum/Theme";
import { showEntityView } from "@app/store/entityView/actions";
import { getLegacyMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTheme } from "@app/store/theme";
import useHtmlCallback from "@app/util/hook/useHtmlCallback";

import googleMapStyles from "../googleMapStyles";
import defaultGeoPointGetter from "../utils/defaultGeoPointGetter";
import defaultMarkerOptionsGetter from "../utils/defaultMarkerOptionsGetter";

import defaultSelectedEntityNodeGetter from "./defaultSelectedEntityNodeGetter";
import Markers from "./Markers";
import styles from "./SimpleEntityMap.module.scss";

type MarkerOptions = google.maps.MarkerOptions;

const defaultContainerStyles = {
  display: "flex",
  position: "relative",
};

interface OwnProps<T> {
  center?: google.maps.LatLng | google.maps.LatLngLiteral;
  children?:
    | ReactElement<{ map?: google.maps.Map }>
    | ReadonlyArray<ReactElement<{ map?: google.maps.Map }>>;
  entities: T[];
  entityType: EntityType;
  getGeoPoint?: (entity: T, entityType: EntityType) => GeoPoint | undefined;
  getMarkerProps?: (
    entity: T,
    entityType: EntityType,
    markerSize: MarkerSize,
    markerIcon: MarkerIcon
  ) => MarkerOptions;
  getPreview?: (
    entity: T,
    entityType: EntityType,
    callbackName?: string,
    companyClickCallbackName?: string,
    personClickCallbackName?: string
  ) => React.ReactNode;
  heatMapWeightKey?: keyof PickWithType<T, number>;
  loading?: boolean;
  onChangeBasemap?: (name: string) => void;
  onClick?: (entity: T, element: HTMLElement) => void;
  onPreview?: (entity: T) => void;
  onSelectPin?: (entity: T) => void;
  showBaseLayerSelector?: boolean;
}

interface Props<T> extends OwnProps<T> {
  mapSettings: { markerIcon: MarkerIcon; markerSize: MarkerSize };
  showEntityView: typeof showEntityView;
  theme: Theme;
}

const SimpleEntityMap = <T extends AnyEntity>({
  center,
  children,
  entities,
  entityType,
  getGeoPoint = defaultGeoPointGetter,
  getMarkerProps = defaultMarkerOptionsGetter,
  getPreview = defaultSelectedEntityNodeGetter,
  loading,
  mapSettings,
  onChangeBasemap,
  onClick,
  onPreview,
  onSelectPin,
  showBaseLayerSelector,
  showEntityView,
  theme,
}: Props<T>) => {
  const intl = useIntl();
  const [map, setMap] = useState<google.maps.Map | undefined>();
  const initMap = useCallback(
    (mapProps?: IMapProps, map?: google.maps.Map) => {
      // Apply default options. Some of these options (controlSize for example) do not work when
      // are set as props of the Map component.
      map?.setOptions({ controlSize: 32, disableDefaultUI: true, zoom: 12, zoomControl: true });
      setMap(map);
    },
    [setMap]
  );

  const basemapStyleOptions = useBasemapStyleOptions(intl);
  const handleBasemapChange = useCallback(
    (value: BaseMapStyle) => {
      map?.setOptions({
        styles: googleMapStyles[value],
      });
      onChangeBasemap?.(value as string);
    },
    [map, onChangeBasemap]
  );

  // update map style when theme changes
  useEffect(() => {
    map?.setOptions({
      styles:
        googleMapStyles[theme === Theme.LIGHT ? BaseMapStyle.STANDARD : BaseMapStyle.MIDNIGHT],
    });
  }, [map, theme]);

  const [selectedMarker, setSelectedMarker] = useState<google.maps.Marker | undefined>();
  const [selectedEntity, setSelectedEntity] = useState<T | undefined>();

  const handleSelectEntity = useCallback(
    (entity: T, marker: google.maps.Marker, element: HTMLElement) => {
      setSelectedMarker(marker);
      setSelectedEntity(entity);
      onSelectPin?.(entity);
      onClick?.(entity, element);
    },
    [onSelectPin, onClick, setSelectedEntity, setSelectedMarker]
  );

  const hideInfoWindow = useCallback(() => {
    setSelectedMarker(undefined);
    setSelectedEntity(undefined);
  }, [setSelectedEntity, setSelectedMarker]);

  // this is a tricky way to communicate with the code in InfoWindows.
  // Those windows are pure HTML code and if we need to get any feedback from there, we also
  // need to define our callback in the global namespace (window).
  const callbackName = useHtmlCallback(() => {
    if (selectedEntity) {
      onPreview?.(selectedEntity);
    }
  }, [onPreview, selectedEntity]);

  const previewCompanyCallbackName = useHtmlCallback(
    (entityId: Company["id"]) => {
      showEntityView({ entityId, entityType: EntityType.COMPANY });
    },
    [onPreview, selectedEntity]
  );

  const previewPersonCallbackName = useHtmlCallback(
    (entityId: Person["id"]) => {
      showEntityView({ entityId, entityType: EntityType.PERSON });
    },
    [showEntityView]
  );

  const entityPreview = selectedEntity
    ? getPreview(
        selectedEntity,
        entityType,
        callbackName,
        previewCompanyCallbackName,
        previewPersonCallbackName
      )
    : null;

  useEffect(() => {
    if (center && map) {
      map.setCenter(center);
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(center);
      map.fitBounds(bounds);
      map.setZoom(14);
    }
  }, [center, map]);

  const childs = useMemo(
    () =>
      map
        ? Children.map(children, (child) =>
            React.isValidElement(child) ? React.cloneElement(child, { ...child.props, map }) : null
          )
        : null,
    [children, map]
  );

  return (
    <Map
      containerStyle={defaultContainerStyles}
      google={window.google}
      loaded // assuming window.google is always loaded
      onClick={hideInfoWindow}
      onReady={initMap}
      styles={googleMapStyles[BaseMapStyle.STANDARD]}
    >
      {(loading || !map) && <LoadingSpinner />}
      {!loading && map && (
        <>
          {showBaseLayerSelector && (
            <Select<BaseMapStyle>
              className={styles.baseLayerSelect}
              defaultValue={BaseMapStyle.STANDARD}
              dropdownMatchSelectWidth={false}
              onChange={handleBasemapChange}
              options={basemapStyleOptions}
            />
          )}

          <Markers
            entities={entities}
            entityType={entityType}
            getGeoPoint={getGeoPoint}
            getMarkerProps={getMarkerProps}
            map={map}
            markerIcon={mapSettings.markerIcon}
            markerSize={mapSettings.markerSize}
            onClick={handleSelectEntity}
          />

          <InfoWindow
            google={window.google}
            map={map}
            // ignoring because it might be undefined according to examples in lib docs
            // @ts-ignore
            marker={selectedMarker}
            // ignoring because such property exists according to lib docs, although missed in TS defs
            // @ts-ignore
            onClose={hideInfoWindow}
            visible={!!selectedMarker && !!selectedEntity && !!entityPreview}
          >
            {
              entityPreview || <></> // InfoWindow requires children to be a ReactElement at least
            }
          </InfoWindow>

          {childs}
        </>
      )}
    </Map>
  );
};

const mapStateToProps = (state: RootState) => ({
  mapSettings: getLegacyMapSettings(state),
  theme: getTheme(state),
});

const mapDispatchToProps = {
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(SimpleEntityMap) as <
  T extends AnyEntity
>(
  props: OwnProps<T>
) => any;
